import { useState, useRef, useEffect } from 'react';
import { Search } from 'lucide-react';
import './LocationFilter.scss';
import { LOCATIONS } from '../../utils/Locations';


const SearchableSelect = ({ 
  options, 
  value, 
  onChange, 
  placeholder, 
  disabled = false 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);



  const filteredOptions = options.filter(option =>
    option.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSelect = (option) => {
    onChange(option);
    setIsOpen(false);
    setSearchTerm('');
  };

  return (
    <div className={`searchable-select ${disabled ? 'disabled' : ''}`} ref={dropdownRef}>
      <div 
        className="searchable-select__header" 
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        <span>{value || placeholder}</span>
        <span className="searchable-select__arrow"></span>
      </div>

      {isOpen && !disabled && (
        <div className="searchable-select__dropdown">
          <div className="searchable-select__search">
            <Search size={16} />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search..."
              onClick={(e) => e.stopPropagation()}
              autoFocus
            />
          </div>
          <div className="searchable-select__options">
            <div 
              className="searchable-select__option"
              onClick={() => handleSelect('')}
            >
              All {placeholder}
            </div>
            {filteredOptions.map((option) => (
              <div
                key={option}
                className={`searchable-select__option ${value === option ? 'selected' : ''}`}
                onClick={() => handleSelect(option)}
              >
                {option}
              </div>
            ))}
            {filteredOptions.length === 0 && (
              <div className="searchable-select__no-results">
                No results found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const OTHER_DISTRICT_LABEL = 'Other (specify below)';

const LocationFilter = ({
  selectedCountry,
  selectedDistrict,
  selectedState = '',
  onCountryChange,
  onDistrictChange,
  onStateChange,
  customCountry = '',
  customDistrict = '',
  onCustomCountryChange,
  onCustomDistrictChange
}) => {
  const isOtherCountry = selectedCountry === 'Other';
  const isIndia = selectedCountry === 'India';
  const loc = selectedCountry && LOCATIONS[selectedCountry];
  const hasStates = loc?.hasStates && loc?.states;

  const states = hasStates ? Object.keys(loc.states) : [];
  const districts = hasStates && selectedState
    ? (loc.states[selectedState] || [])
    : (loc?.districts || []);
  const hasOtherDistrictOption = districts.length > 0 && !loc?.isOther;
  const districtsWithOther = hasOtherDistrictOption ? [...districts, OTHER_DISTRICT_LABEL] : [];

  useEffect(() => {
    if (!selectedCountry) {
      onCountryChange('Kerala');
    }
  }, []);

  const handleCountryChange = (value) => {
    onCountryChange(value);
    onDistrictChange('');
    onStateChange?.('');
    onCustomCountryChange?.('');
    onCustomDistrictChange?.('');
  };

  const handleStateChange = (value) => {
    onStateChange?.(value);
    onDistrictChange('');
    onCustomDistrictChange?.('');
  };

  const handleDistrictChange = (value) => {
    if (value === OTHER_DISTRICT_LABEL) {
      onDistrictChange(OTHER_DISTRICT_LABEL);
    } else {
      onDistrictChange(value);
      onCustomDistrictChange?.('');
    }
  };

  const isOtherDistrict = selectedDistrict === OTHER_DISTRICT_LABEL;
  const displayDistrictValue = isOtherDistrict && customDistrict ? customDistrict : selectedDistrict;

  return (
    <div className="filter location-filter">
      <div className="filter__group">
        <SearchableSelect
          options={Object.keys(LOCATIONS).filter(k => !LOCATIONS[k]?.isOther || k === 'Other')}
          value={selectedCountry}
          onChange={handleCountryChange}
          placeholder="Select Country"
        />

        {isOtherCountry ? (
          <div className="location-filter__custom">
            <input
              type="text"
              className="location-filter__custom-input"
              placeholder="Country or Region (e.g. Pakistan, Sri Lanka)"
              value={customCountry}
              onChange={(e) => onCustomCountryChange?.(e.target.value)}
            />
            <input
              type="text"
              className="location-filter__custom-input"
              placeholder="District or City (e.g. Lahore, Colombo)"
              value={customDistrict}
              onChange={(e) => onCustomDistrictChange?.(e.target.value)}
            />
          </div>
        ) : isIndia ? (
          <>
            <SearchableSelect
              options={states}
              value={selectedState}
              onChange={handleStateChange}
              placeholder="Select State"
              disabled={!selectedCountry}
            />
            <SearchableSelect
              options={districtsWithOther}
              value={displayDistrictValue}
              onChange={handleDistrictChange}
              placeholder={selectedState ? 'Select City' : 'Select State First'}
              disabled={!selectedState}
            />
            {isOtherDistrict && (
              <input
                type="text"
                className="location-filter__custom-input location-filter__custom-input--single"
                placeholder="Enter city (e.g. Whitefield, Koramangala)"
                value={customDistrict}
                onChange={(e) => onCustomDistrictChange?.(e.target.value)}
              />
            )}
          </>
        ) : (
          <>
            <SearchableSelect
              options={districtsWithOther}
              value={displayDistrictValue}
              onChange={handleDistrictChange}
              placeholder={selectedCountry ? `Select ${selectedCountry === 'Kerala' ? 'District' : 'City'}` : 'Select Country First'}
              disabled={!selectedCountry}
            />
            {isOtherDistrict && (
              <input
                type="text"
                className="location-filter__custom-input location-filter__custom-input--single"
                placeholder="Enter district or city (e.g. Kondotty, Mangalore)"
                value={customDistrict}
                onChange={(e) => onCustomDistrictChange?.(e.target.value)}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};
export const getLocationDisplayName = (country, district, customCountry = '', customDistrict = '', state = '') => {
  if (!country) return '';
  if (country === 'Other') {
    return customDistrict && customCountry ? `${customDistrict}, ${customCountry}` : customCountry || customDistrict || 'Other';
  }
  if (district === 'Other (specify below)' && customDistrict) {
    const region = country === 'India' && state ? state : (LOCATIONS[country]?.name || country);
    return `${customDistrict}, ${region}`;
  }
  if (!district) return state || LOCATIONS[country]?.name || country;
  const region = country === 'India' && state ? state : (LOCATIONS[country]?.name || country);
  return `${district}, ${region}`;
};

export default LocationFilter;